import React, {
  useState,
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from 'react'
import Web3Modal from 'web3modal'
import {
  StaticJsonRpcProvider,
  JsonRpcProvider,
  Web3Provider,
} from '@ethersproject/providers'
import WalletConnectProvider from '@walletconnect/web3-provider'

/**
 * kept as function to mimic `getMainnetURI()`
 * @returns string
 */
function getTestnetURI() {
  return ''
}

/**
 * "intelligently" loadbalances production API Keys
 * @returns string
 */
function getMainnetURI() {
  return 'https://rpc.Dexit.network'
}

const Web3Context = React.createContext(null)

export const useWeb3Context = () => {
  const web3Context = useContext(Web3Context)
  if (!web3Context) {
    throw new Error(
      'useWeb3Context() can only be used inside of <Web3ContextProvider />, ' +
        'please declare it at a higher level.',
    )
  }
  const { onChainProvider } = web3Context
  return useMemo(() => {
    return { ...onChainProvider }
  }, [web3Context])
}

export const useAddress = () => {
  const { address } = useWeb3Context()
  return address
}

export const Web3ContextProvider = ({ children }) => {
  const [connected, setConnected] = useState(false)
  // NOTE (appleseed): if you are testing on rinkeby you need to set chainId === 4 as the default for non-connected wallet testing...
  // ... you also need to set getTestnetURI() as the default uri state below
  const [chainID, setChainID] = useState(877)
  const [address, setAddress] = useState('')

  const [uri, setUri] = useState(getMainnetURI())

  const [provider, setProvider] = useState(new StaticJsonRpcProvider(uri))

  const [web3Modal, setWeb3Modal] = useState(
    new Web3Modal({
      // network: "mainnet", // optional
      cacheProvider: true, // optional
      providerOptions: {
        walletconnect: {
          package: WalletConnectProvider,
          options: {
            rpc: {
              877: getMainnetURI(),
            },
          },
        },
      },
    }),
  )

  const hasCachedProvider = () => {
    if (!web3Modal) return false
    if (!web3Modal.cachedProvider) return false
    return true
  }

  // NOTE (appleseed): none of these listeners are needed for Backend API Providers
  // ... so I changed these listeners so that they only apply to walletProviders, eliminating
  // ... polling to the backend providers for network changes
  const _initListeners = useCallback(
    (rawProvider) => {
      if (!rawProvider.on) {
        return
      }
      rawProvider.on('accountsChanged', async (accounts) => {
        setTimeout(() => window.location.reload(), 1)
      })

      rawProvider.on('chainChanged', async (chain) => {
        _checkNetwork(chain)
        setTimeout(() => window.location.reload(), 1)
      })

      rawProvider.on('network', (_newNetwork, oldNetwork) => {
        if (!oldNetwork) return
        window.location.reload()
      })
    },
    [provider],
  )

  /**
   * throws an error if networkID is not 1 (mainnet) or 4 (rinkeby)
   */
  const _checkNetwork = (otherChainID) => {
    if (chainID !== otherChainID) {
      console.warn('You are switching networks')
      if (otherChainID === 877) {
        setChainID(otherChainID)
        otherChainID === 877 ? setUri(getMainnetURI()) : setUri(getTestnetURI())
        return true
      }
      return false
    }
    return true
  }

  // connect - only runs for WalletProviders
  const connect = useCallback(async () => {
    const rawProvider = await web3Modal.connect()

    // new _initListeners implementation matches Web3Modal Docs
    // ... see here: https://github.com/Web3Modal/web3modal/blob/2ff929d0e99df5edf6bb9e88cff338ba6d8a3991/example/src/App.tsx#L185
    _initListeners(rawProvider)
    const connectedProvider = new Web3Provider(rawProvider, 'any')
    const chainId = await connectedProvider
      .getNetwork()
      .then((network) => network.chainId)
    const connectedAddress = await connectedProvider.getSigner().getAddress()
    const validNetwork = _checkNetwork(chainId)
    if (!validNetwork) {
      console.error('Wrong network, please switch to DEXIT')
      alert('Wrong network, please switch to DEXIT')
      return
    }
    // Save everything after we've validated the right network.
    // Eventually we'll be fine without doing network validations.
    setAddress(connectedAddress)
    setProvider(connectedProvider)

    // Keep this at the bottom of the method, to ensure any repaints have the data we need
    setConnected(true)

    return connectedProvider
  }, [provider, web3Modal, connected])

  const disconnect = useCallback(async () => {
    console.log('disconnecting')
    web3Modal.clearCachedProvider()
    setConnected(false)

    setTimeout(() => {
      window.location.reload()
    }, 1)
  }, [provider, web3Modal, connected])

  const onChainProvider = useMemo(
    () => ({
      connect,
      disconnect,
      hasCachedProvider,
      provider,
      connected,
      address,
      chainID,
      web3Modal,
      uri,
    }),
    [
      connect,
      disconnect,
      hasCachedProvider,
      provider,
      connected,
      address,
      chainID,
      web3Modal,
      uri,
    ],
  )

  return (
    <Web3Context.Provider value={{ onChainProvider }}>
      {children}
    </Web3Context.Provider>
  )
}
